// src/onus_components/OnlineStatusModal.jsx — si la ONT está en línea y cómo
//
// Consulta: se lanza sola al abrir. Busca la ONT en la OLT por su SERIE
// (`display ont info by-sn`). Por la descripción no sirve: `by-desc 7`
// trae a todos los `user…7…` de la OLT y el primero no es el cliente.

import React, { useCallback, useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Iconos } from "../componentes/base";
import ModalPasos, { fichaOnu, leerRespuesta } from "../componentes/olt/ModalPasos";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// "2026-09-13 23:23:15-05:00" → "13 sep 23:23"
export function fechaCorta(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/.exec(v || "");
  return m ? `${Number(m[3])} ${MESES[Number(m[2]) - 1]} ${m[4]}:${m[5]}` : (v || null);
}

// "0 day(s), 4 hour(s), 5 minute(s), 13 second(s)" → "4 h 5 min"
export function duracionCorta(v) {
  const n = (u) => Number((new RegExp(`(\\d+)\\s*${u}`).exec(v || "") || [])[1] || 0);
  const d = n("day"), h = n("hour"), mi = n("minute"), s = n("second");
  if (!v) return null;
  if (d) return `${d} d ${h} h`;
  if (h) return `${h} h ${mi} min`;
  if (mi) return `${mi} min`;
  return `${s} s`;
}

// Lo que quiere decir la causa de la última caída, en cristiano.
export function causaEnClaro(c) {
  const t = String(c || "").toLowerCase();
  if (!t) return null;
  if (t.includes("dying")) return "se quedó sin corriente";
  if (/\blo[sb]i?\b|losi|lobi/.test(t)) return "sin señal de fibra";
  if (t.includes("lof")) return "señal óptica mala";
  if (/\bs[fd]i\b/.test(t)) return "señal óptica degradada";
  if (t.includes("reset") || t.includes("re-register")) return "la reiniciaron";
  if (t.includes("deactiv")) return "la desactivaron en la OLT";
  return null;
}

function lectura(s, onu) {
  if (!s.found) {
    return { tono: "mal", pastilla: "NO ESTÁ EN LA OLT",
      texto: "Esa serie no está dada de alta en la OLT." };
  }
  const donde = s.otro_lugar
    ? ` Ojo: la OLT la tiene en ${s.fsp} ONT ${s.ont_id} y la base dice ${onu?.fsp ?? "?"} ONT ${onu?.ont_id ?? "?"}.`
    : "";
  if (s.control_flag && s.control_flag.startsWith("deactiv")) {
    return { tono: "mal", pastilla: "DESACTIVADA",
      texto: `La ONT está desactivada en la OLT: no va a subir hasta activarla.${donde}` };
  }
  if (!s.online) {
    const causa = s.ultima_causa ? ` · ${s.ultima_causa}${causaEnClaro(s.ultima_causa) ? `: ${causaEnClaro(s.ultima_causa)}` : ""}` : "";
    const desde = s.ultima_caida ? `Fuera de línea desde el ${fechaCorta(s.ultima_caida)}` : "La ONT no está en línea";
    return { tono: "mal", pastilla: "FUERA DE LÍNEA", texto: `${desde}${causa}.${donde}` };
  }
  if (s.config_state && s.config_state !== "normal") {
    return { tono: "aviso", pastilla: "EN LÍNEA",
      texto: `En línea, pero la configuración quedó en «${s.config_state}»: la ONT no aceptó toda la configuración.${donde}` };
  }
  const tiempo = duracionCorta(s.en_linea_desde);
  return { tono: s.otro_lugar ? "aviso" : "bien", pastilla: "EN LÍNEA",
    texto: `${tiempo ? `En línea desde hace ${tiempo}.` : "La ONT está en línea."}${donde}` };
}

export default function OnlineStatusModal({ open, onu, onClose }) {
  const [estado, setEstado] = useState("reposo");
  const [datos, setDatos] = useState(null);
  const [salida, setSalida] = useState([]);
  const [error, setError] = useState("");

  const consultar = useCallback(async () => {
    if (!onu?.id) return;
    setEstado("corriendo"); setDatos(null); setSalida([]); setError("");
    try {
      const res = await authFetch(`/api/onus/${onu.id}/online_status`);
      const data = await leerRespuesta(res);
      setSalida(Array.isArray(data?.outputs) ? data.outputs : []);
      if (!res.ok || !data?.ok) throw new Error(data?.mensaje || "No se pudo consultar el estado.");
      setDatos(data);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setEstado("fin");
    }
  }, [onu?.id]);

  useEffect(() => { if (open) consultar(); }, [open, consultar]);

  const s = datos?.status || {};
  const info = { ...onu, ...(datos?.onu || {}) };
  const l = datos ? lectura(s, info) : null;
  const bien = l?.tono !== "mal";

  const casillas = !s.found ? [] : [
    ["Run state", s.run_state, s.online ? "bien" : "mal"],
    ["Configuración", s.config_state, s.config_state === "normal" ? "bien" : "aviso"],
    ["Control", s.control_flag, s.control_flag === "active" ? undefined : "mal"],
    ["Match state", s.match_state],
    ...(s.online ? [
      ["Distancia", s.distancia_m != null ? `${(s.distancia_m / 1000).toFixed(2)} km` : null],
      ["Temperatura", s.temperatura_c != null ? `${s.temperatura_c} °C` : null],
      ["CPU / memoria", s.cpu_pct != null ? `${s.cpu_pct}% / ${s.memoria_pct ?? "—"}%` : null],
      ["Subió", fechaCorta(s.ultima_subida)],
    ] : [
      ["Se cayó", fechaCorta(s.ultima_caida)],
      ["Causa", s.ultima_causa, "mal"],
      ["Había subido", fechaCorta(s.ultima_subida)],
    ]),
    ["Perfil", s.line_profile ? `${s.line_profile} (${s.line_profile_id}/${s.srv_profile_id ?? "?"})` : null],
    ["Descripción", s.descripcion],
    ...(s.otro_lugar ? [["En la OLT", `${s.fsp} · ONT ${s.ont_id}`, "aviso"]] : []),
  ];

  return (
    <ModalPasos
      abierto={open}
      titulo="Estado de la ONT"
      icono={Iconos.senal}
      ficha={fichaOnu(info)}
      consulta
      estado={estado}
      salida={salida}
      error={error}
      textoEspera="Buscando la ONT en la OLT"
      mensaje={l?.texto}
      tono={l?.tono}
      resultado={l && (
        <>
          <span className={`mp-grande mp-grande--${bien ? "bien" : "mal"}`}>
            {bien ? <Iconos.enLinea size={15} strokeWidth={2.2} /> : <Iconos.fueraDeLinea size={15} strokeWidth={2.2} />}
            {l.pastilla}
          </span>
          {casillas.length > 0 && (
            <dl className="mp-res">
              {casillas.map(([k, v, t]) => (
                <div key={k} className={v && t ? `mp-res--${t}` : undefined}><dt>{k}</dt><dd>{v || "—"}</dd></div>
              ))}
            </dl>
          )}
        </>
      )}
      fases={[
        { rotulo: "Buscar la ONT por su serie", coincide: /^display ont info/, cmds: [`display ont info by-sn ${onu?.serial || "?"}`] },
      ]}
      onEjecutar={consultar}
      onCerrar={onClose}
    />
  );
}
